import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { 
  FileText, Search, CheckCircle2, Clock, AlertCircle, 
  Building2, Send, Calculator, Eye, Package, Calendar,
  RefreshCw, ChevronRight, IndianRupee, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Pipeline status configurations
const PIPELINE_STATUS = {
  awaiting_invoice: { label: 'Awaiting Invoice', color: 'bg-gray-100 text-gray-600', step: 0 },
  awaiting_handover: { label: 'Awaiting Handover', color: 'bg-yellow-100 text-yellow-700', step: 1 },
  pending_acknowledgement: { label: 'Pending Acknowledgement', color: 'bg-blue-100 text-blue-700', step: 2 },
  pending_tally: { label: 'Pending Tally Entry', color: 'bg-purple-100 text-purple-700', step: 3 },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', step: 4 }
};

export default function GRNDocumentsPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending_acknowledgement');
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState({ pending_acknowledgement: 0, pending_tally: 0 });
  
  // Detail dialog
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Payment section state
  const [paymentForm, setPaymentForm] = useState({
    payment_invoice_amount: '',
    payment_advance_paid: '',
    payment_due_date: ''
  });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Check if user can edit payment information (Purchase team only, NOT Accounts)
  const canEditPaymentInfo = () => {
    if (!user) return false;
    if (user.is_owner || user.is_super_admin) return true;
    
    // Accounts team users (those with grn_documents but not purchase modules) cannot edit
    const hasAccountsModule = user.allowed_modules?.includes('grn_documents') || 
                              user.allowed_modules?.includes('accounts_payments');
    const hasPurchaseModule = user.allowed_modules?.includes('purchase_entry') || 
                              user.allowed_modules?.includes('purchase_orders');
    
    // Only allow if user has purchase modules AND is not purely an accounts user
    // If user has ONLY accounts modules (grn_documents, accounts_payments) without purchase modules, deny edit
    if (hasAccountsModule && !hasPurchaseModule) {
      return false;
    }
    
    // User must have purchase modules to edit payment info
    return hasPurchaseModule;
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', activeTab);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '200');
      
      const response = await axios.get(
        `${API_URL}/api/accounts/grn-documents?${params}`,
        { headers: getAuthHeader() }
      );
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || { pending_acknowledgement: 0, pending_tally: 0 });
    } catch (error) {
      console.error('Error fetching GRN documents:', error);
      toast.error('Failed to load GRN documents');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAcknowledge = async (entryId) => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/store/purchase-entries/${entryId}/docs-received`,
        { docs_received: true },
        { headers: getAuthHeader() }
      );
      toast.success('Documents acknowledged successfully');
      fetchEntries();
      // Update selected entry if dialog is open
      if (selectedEntry?.id === entryId) {
        setSelectedEntry(prev => ({ ...prev, docs_received: true, pipeline_status: 'pending_tally' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge documents');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkTally = async (entryId) => {
    setActionLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/store/purchase-entries/${entryId}/tally-recorded`,
        { tally_recorded: true },
        { headers: getAuthHeader() }
      );
      toast.success('Tally entry marked as recorded');
      fetchEntries();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark tally recorded');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSavePaymentInfo = async () => {
    if (!selectedEntry) return;
    
    setPaymentSaving(true);
    try {
      const response = await axios.patch(
        `${API_URL}/api/store/purchase-entries/${selectedEntry.id}/payment-info`,
        {
          payment_invoice_amount: paymentForm.payment_invoice_amount ? parseFloat(paymentForm.payment_invoice_amount) : null,
          payment_advance_paid: paymentForm.payment_advance_paid ? parseFloat(paymentForm.payment_advance_paid) : 0,
          payment_due_date: paymentForm.payment_due_date || null
        },
        { headers: getAuthHeader() }
      );
      
      // Update selected entry with new payment info
      setSelectedEntry(prev => ({
        ...prev,
        payment_invoice_amount: response.data.payment_invoice_amount,
        payment_advance_paid: response.data.payment_advance_paid,
        payment_balance: response.data.payment_balance,
        payment_due_date: response.data.payment_due_date
      }));
      
      toast.success('Payment information saved');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment information');
    } finally {
      setPaymentSaving(false);
    }
  };

  // Calculate balance for display
  const getPaymentBalance = () => {
    const invoiceAmt = parseFloat(paymentForm.payment_invoice_amount) || 0;
    const advancePaid = parseFloat(paymentForm.payment_advance_paid) || 0;
    return invoiceAmt - advancePaid;
  };

  const openDetailDialog = (entry) => {
    setSelectedEntry(entry);
    // Initialize payment form with existing values or defaults from grand_total
    setPaymentForm({
      payment_invoice_amount: entry.payment_invoice_amount ?? entry.grand_total ?? '',
      payment_advance_paid: entry.payment_advance_paid ?? '',
      payment_due_date: entry.payment_due_date ? entry.payment_due_date.split('T')[0] : ''
    });
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusConfig = (status) => {
    return PIPELINE_STATUS[status] || PIPELINE_STATUS.awaiting_invoice;
  };

  const canAcknowledge = (entry) => {
    return entry.docs_handed_over && !entry.docs_received;
  };

  const canMarkTally = (entry) => {
    return entry.docs_received && !entry.tally_recorded;
  };

  const isActionDisabled = (entry) => {
    return !entry.docs_handed_over;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="grn-documents-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6" />
              GRN Documents
            </h1>
            <p className="text-muted-foreground">Acknowledge receipt and record tally entries for purchase invoices</p>
          </div>
          <Button variant="outline" onClick={fetchEntries} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filter Tabs and Search */}
        <div className="flex justify-between items-center gap-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
            <TabsList>
              <TabsTrigger value="pending_acknowledgement" className="gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Pending Acknowledgement
                <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700">
                  {summary.pending_acknowledgement}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="pending_tally" className="gap-2">
                <Calculator className="w-4 h-4" />
                Pending Tally Entry
                <Badge variant="secondary" className="ml-1 bg-purple-100 text-purple-700">
                  {summary.pending_tally}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="completed" className="gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Completed
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by GRN, supplier, invoice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Table Content */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{activeTab === 'completed' ? 'No completed GRN documents' : 'No pending GRN documents'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>GRN / Store Receipt</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => {
                    const statusConfig = getStatusConfig(entry.pipeline_status);
                    const disabled = isActionDisabled(entry);
                        
                        return (
                          <TableRow 
                            key={entry.id} 
                            className={disabled ? 'opacity-50 bg-gray-50' : 'hover:bg-muted/50'}
                          >
                            <TableCell>
                              <div>
                                <p className="font-mono font-medium">{entry.store_receipt_no || entry.grn_no}</p>
                                <p className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium">{entry.supplier_name}</p>
                            </TableCell>
                            <TableCell>
                              {entry.invoice_no || entry.invoice_dc_no ? (
                                <div>
                                  <p className="font-mono text-sm">{entry.invoice_no || entry.invoice_dc_no}</p>
                                  {entry.invoice_received && (
                                    <Badge variant="outline" className="text-xs text-green-600">Received</Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(entry.grand_total)}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className={entry.days_since_grn > 7 ? 'text-red-600 font-medium' : ''}>
                                {entry.days_since_grn || 0}d
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openDetailDialog(entry)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {canAcknowledge(entry) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAcknowledge(entry.id)}
                                    disabled={actionLoading}
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Acknowledge
                                  </Button>
                                )}
                                {canMarkTally(entry) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleMarkTally(entry.id)}
                                    disabled={actionLoading}
                                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                                  >
                                    <Calculator className="w-4 h-4 mr-1" />
                                    Mark Tally
                                  </Button>
                                )}
                                {disabled && (
                                  <span className="text-xs text-muted-foreground italic px-2 py-1">
                                    Awaiting handover
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                GRN Details - {selectedEntry?.store_receipt_no || selectedEntry?.grn_no}
              </DialogTitle>
              <DialogDescription>
                View GRN details and manage document acknowledgement
              </DialogDescription>
            </DialogHeader>

            {selectedEntry && (
              <div className="space-y-6 py-4">
                {/* Pipeline Status */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-8">
                    {Object.entries(PIPELINE_STATUS).slice(0, 5).map(([key, config], idx) => {
                      const isActive = (selectedEntry.pipeline_step || 0) >= config.step;
                      const isCurrent = selectedEntry.pipeline_status === key;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                            isActive ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                          } ${isCurrent ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}>
                            {isActive ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                          </div>
                          <span className={`text-xs ${isCurrent ? 'font-semibold' : 'text-muted-foreground'}`}>
                            {config.label.split(' ')[0]}
                          </span>
                          {idx < 4 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Supplier</Label>
                    <p className="font-medium">{selectedEntry.supplier_name}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Label className="text-xs text-muted-foreground">Invoice No</Label>
                    <p className="font-mono">{selectedEntry.invoice_no || selectedEntry.invoice_dc_no || '-'}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Label className="text-xs text-muted-foreground">GRN Date</Label>
                    <p>{formatDate(selectedEntry.created_at)}</p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-2 font-medium text-sm">Line Items</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedEntry.items || []).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell className="text-right">
                            {item.supplier_qty || item.system_qty || item.quantity_received} {item.supplier_unit || item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.supplier_rate || item.system_rate || 0)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.supplier_amount || item.system_amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-72 space-y-2 p-4 bg-muted/30 rounded-lg">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatCurrency(selectedEntry.total_supplier_amount || selectedEntry.total_system_amount)}</span>
                    </div>
                    {(selectedEntry.other_charges_taxable || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Other Charges (Taxable)</span>
                        <span>{formatCurrency(selectedEntry.other_charges_taxable)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">GST ({selectedEntry.gst_rate || 0}%)</span>
                      <span>{formatCurrency(selectedEntry.gst_amount)}</span>
                    </div>
                    {(selectedEntry.other_charges_non_taxable || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Other Charges (Non-Taxable)</span>
                        <span>{formatCurrency(selectedEntry.other_charges_non_taxable)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Grand Total</span>
                      <span>{formatCurrency(selectedEntry.grand_total)}</span>
                    </div>
                  </div>
                </div>

                {/* Handover Info */}
                {selectedEntry.docs_handed_over && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Send className="w-4 h-4" />
                      <span className="font-medium">Documents Handed Over</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">
                      {formatDate(selectedEntry.docs_handed_over_at)} by {selectedEntry.docs_handed_over_by}
                    </p>
                  </div>
                )}

                {/* Payment Section */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 font-medium text-sm flex items-center justify-between text-green-800">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-4 h-4" />
                      Payment Information
                    </div>
                    {!canEditPaymentInfo() && (
                      <Badge variant="outline" className="text-xs bg-white">View Only</Badge>
                    )}
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Warning if balance > 0 and invoice not received */}
                    {getPaymentBalance() > 0 && !selectedEntry.invoice_received && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium">Invoice Not Received</p>
                          <p className="text-amber-700">Balance payment can only be made after invoice is received.</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="payment_invoice_amount" className="text-sm">
                          Total Invoice Amount (₹)
                        </Label>
                        <Input
                          id="payment_invoice_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Enter invoice amount"
                          value={paymentForm.payment_invoice_amount}
                          onChange={(e) => setPaymentForm(prev => ({
                            ...prev,
                            payment_invoice_amount: e.target.value
                          }))}
                          disabled={!canEditPaymentInfo()}
                          className={!canEditPaymentInfo() ? 'bg-muted cursor-not-allowed' : ''}
                          data-testid="payment-invoice-amount"
                        />
                        {canEditPaymentInfo() && (
                          <p className="text-xs text-muted-foreground">
                            Auto-filled from GRN total. Edit if actual payable differs.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="payment_advance_paid" className="text-sm">
                          Advance Paid (₹)
                        </Label>
                        <Input
                          id="payment_advance_paid"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Enter advance amount"
                          value={paymentForm.payment_advance_paid}
                          onChange={(e) => setPaymentForm(prev => ({
                            ...prev,
                            payment_advance_paid: e.target.value
                          }))}
                          disabled={!canEditPaymentInfo()}
                          className={!canEditPaymentInfo() ? 'bg-muted cursor-not-allowed' : ''}
                          data-testid="payment-advance-paid"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <Label className="text-xs text-muted-foreground">Balance to be Paid</Label>
                        <p className={`text-xl font-bold ${getPaymentBalance() > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(getPaymentBalance())}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="payment_due_date" className="text-sm">
                          Payment Due Date
                        </Label>
                        <Input
                          id="payment_due_date"
                          type="date"
                          value={paymentForm.payment_due_date}
                          onChange={(e) => setPaymentForm(prev => ({
                            ...prev,
                            payment_due_date: e.target.value
                          }))}
                          disabled={!canEditPaymentInfo() || getPaymentBalance() <= 0}
                          className={(!canEditPaymentInfo() || getPaymentBalance() <= 0) ? 'bg-muted cursor-not-allowed' : ''}
                          data-testid="payment-due-date"
                        />
                        {getPaymentBalance() <= 0 && canEditPaymentInfo() && (
                          <p className="text-xs text-muted-foreground">
                            No due date needed when balance is zero or negative.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Save button - only visible for purchase team */}
                    {canEditPaymentInfo() && (
                      <div className="flex justify-end pt-2 border-t">
                        <Button
                          size="sm"
                          onClick={handleSavePaymentInfo}
                          disabled={paymentSaving}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="save-payment-btn"
                        >
                          {paymentSaving ? 'Saving...' : 'Save Payment Info'}
                        </Button>
                      </div>
                    )}

                    {/* Last updated info */}
                    {selectedEntry.payment_updated_at && (
                      <p className="text-xs text-muted-foreground text-right">
                        Last updated: {formatDate(selectedEntry.payment_updated_at)} by {selectedEntry.payment_updated_by}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
              {selectedEntry && canAcknowledge(selectedEntry) && (
                <Button
                  onClick={() => handleAcknowledge(selectedEntry.id)}
                  disabled={actionLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Acknowledge Receipt
                </Button>
              )}
              {selectedEntry && canMarkTally(selectedEntry) && (
                <Button
                  onClick={() => handleMarkTally(selectedEntry.id)}
                  disabled={actionLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Mark Tally Recorded
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
